import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Bold, Italic, Underline, Link2, List, ListOrdered, Quote, Image as ImageIcon, Code, Heading1, Heading2, Heading3, Save, Send, ArrowLeft, X, Plus, Loader2, AlertCircle, CheckCircle2, Search, ChevronDown, ChevronUp, FileText, Tag, Globe } from 'lucide-react';
import Header from '../../../components/navigation/Header';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const CATEGORIES = [
  'Dating Tips', 'Photo Tips', 'Science', 'AI & Tech', 'Style', 'Confidence', 'General'
];

const SUGGESTED_TAGS = [
  'dating', 'photos', 'profile', 'tips', 'ai', 'confidence', 'style', 'lighting',
  'angles', 'psychology', 'attraction', 'online-dating', 'selfies', 'posing'
];

const generateSlug = (title) =>
  title?.toLowerCase()
    ?.replace(/[^a-z0-9\s-]/g, '')
    ?.replace(/\s+/g, '-')
    ?.replace(/-+/g, '-')
    ?.trim() || '';

const estimateReadTime = (html) => {
  const text = html?.replace(/<[^>]*>/g, '') || '';
  const words = text?.trim()?.split(/\s+/)?.length || 0;
  const mins = Math.max(1, Math.round(words / 200));
  return `${mins} min read`;
};

const BlogEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const isEditing = Boolean(id);

  // Editor state
  const [mode, setMode] = useState('visual'); // 'visual' | 'html'
  const [htmlContent, setHtmlContent] = useState('');
  const [activeTab, setActiveTab] = useState('content'); // 'content' | 'seo'
  const [seoOpen, setSeoOpen] = useState(false);

  // Form state
  const [form, setForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    category: 'General',
    tags: [],
    status: 'draft',
    cover_image_url: '',
    cover_image_alt: '',
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    og_image_url: '',
    canonical_url: '',
  });

  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditing);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Load existing post
  useEffect(() => {
    if (!isEditing) return;
    const loadPost = async () => {
      setFetchLoading(true);
      try {
        const { data, error: fetchError } = await supabase?.from('blog_posts')?.select('*')?.eq('id', id)?.single();
        if (fetchError) throw fetchError;
        if (data) {
          setForm({
            title: data?.title || '',
            slug: data?.slug || '',
            excerpt: data?.excerpt || '',
            category: data?.category || 'General',
            tags: data?.tags || [],
            status: data?.status || 'draft',
            cover_image_url: data?.cover_image_url || '',
            cover_image_alt: data?.cover_image_alt || '',
            seo_title: data?.seo_title || '',
            seo_description: data?.seo_description || '',
            seo_keywords: data?.seo_keywords || '',
            og_image_url: data?.og_image_url || '',
            canonical_url: data?.canonical_url || '',
          });
          setHtmlContent(data?.content || '');
          setSlugManuallyEdited(true);
        }
      } catch (err) {
        setError(err?.message || 'Failed to load post');
      } finally {
        setFetchLoading(false);
      }
    };
    loadPost();
  }, [id, isEditing]);

  // Sync visual editor content to htmlContent
  useEffect(() => {
    if (mode === 'visual' && editorRef?.current) {
      editorRef.current.innerHTML = htmlContent;
    }
  }, [mode]);

  const syncFromEditor = () => {
    if (editorRef?.current) {
      setHtmlContent(editorRef?.current?.innerHTML);
    }
  };

  const handleTitleChange = (e) => {
    const title = e?.target?.value;
    setForm(prev => ({
      ...prev,
      title,
      ...(!slugManuallyEdited ? { slug: generateSlug(title) } : {}),
    }));
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'slug') setSlugManuallyEdited(true);
  };

  const addTag = (tag) => {
    const clean = tag?.trim()?.toLowerCase()?.replace(/\s+/g, '-');
    if (clean && !form?.tags?.includes(clean)) {
      setForm(prev => ({ ...prev, tags: [...prev?.tags, clean] }));
    }
    setTagInput('');
  };

  const removeTag = (tag) => {
    setForm(prev => ({ ...prev, tags: prev?.tags?.filter(t => t !== tag) }));
  };

  // Rich text commands
  const execCmd = (cmd, value = null) => {
    editorRef?.current?.focus();
    document.execCommand(cmd, false, value);
    syncFromEditor();
  };

  const insertHeading = (level) => {
    editorRef?.current?.focus();
    document.execCommand('formatBlock', false, `h${level}`);
    syncFromEditor();
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) execCmd('createLink', url);
  };

  const insertBlockquote = () => {
    editorRef?.current?.focus();
    document.execCommand('formatBlock', false, 'blockquote');
    syncFromEditor();
  };

  const insertCode = () => {
    editorRef?.current?.focus();
    document.execCommand('formatBlock', false, 'pre');
    syncFromEditor();
  };

  // Image upload for inline editor images
  const handleInlineImageUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const ext = file?.name?.split('.')?.pop();
      const path = `inline/${Date.now()}-${Math.random()?.toString(36)?.slice(2)}.${ext}`;
      const { error: uploadError } = await supabase?.storage?.from('blog-images')?.upload(path, file, { cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase?.storage?.from('blog-images')?.getPublicUrl(path);
      editorRef?.current?.focus();
      document.execCommand('insertImage', false, publicUrl);
      syncFromEditor();
    } catch (err) {
      setError('Image upload failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setImageUploading(false);
      if (fileInputRef?.current) fileInputRef.current.value = '';
    }
  };

  // Cover image upload
  const handleCoverImageUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const ext = file?.name?.split('.')?.pop();
      const path = `covers/${Date.now()}-${Math.random()?.toString(36)?.slice(2)}.${ext}`;
      const { error: uploadError } = await supabase?.storage?.from('blog-images')?.upload(path, file, { cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase?.storage?.from('blog-images')?.getPublicUrl(path);
      setForm(prev => ({ ...prev, cover_image_url: publicUrl }));
    } catch (err) {
      setError('Cover image upload failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setCoverUploading(false);
    }
  };

  const getContent = () => {
    if (mode === 'visual' && editorRef?.current) {
      return editorRef?.current?.innerHTML;
    }
    return htmlContent;
  };

  const handleSave = async (publishStatus = null) => {
    setLoading(true);
    setError(null);
    setSaved(false);
    try {
      const content = getContent();
      const saveData = {
        ...form,
        content,
        read_time: estimateReadTime(content),
        author_id: user?.id,
        ...(publishStatus ? { status: publishStatus } : {}),
        ...(publishStatus === 'published' && form?.status !== 'published'
          ? { published_at: new Date()?.toISOString() }
          : {}),
      };

      if (!saveData?.title?.trim()) {
        setError('Title is required');
        setLoading(false);
        return;
      }
      if (!saveData?.slug?.trim()) {
        setError('Slug is required');
        setLoading(false);
        return;
      }

      let result;
      if (isEditing) {
        result = await supabase?.from('blog_posts')?.update(saveData)?.eq('id', id)?.select()?.single();
      } else {
        result = await supabase?.from('blog_posts')?.insert(saveData)?.select()?.single();
      }

      if (result?.error) throw result?.error;
      setSaved(true);
      if (publishStatus) {
        setForm(prev => ({ ...prev, status: publishStatus }));
      }
      if (!isEditing && result?.data?.id) {
        navigate(`/blog-admin/editor/${result?.data?.id}`, { replace: true });
      }
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save post');
    } finally {
      setLoading(false);
    }
  };

  const seoTitleLength = form?.seo_title?.length || 0;
  const seoDescLength = form?.seo_description?.length || 0;

  if (fetchLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-body">
      <Header user={user} onNavigate={() => {}} onPurchaseCredits={() => {}} onLogout={() => {}} />

      {/* Top bar */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/blog-admin" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-violet-600 transition-colors">
              <ArrowLeft size={15} /> Posts
            </Link>
            <span className="text-gray-300">/</span>
            <span className="text-sm font-semibold text-gray-800 truncate max-w-xs">
              {form?.title || 'New Post'}
            </span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
              form?.status === 'published' ?'bg-emerald-50 text-emerald-700 border border-emerald-200' :'bg-amber-50 text-amber-700 border border-amber-200'
            }`}>
              {form?.status}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {saved && (
              <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                <CheckCircle2 size={13} /> Saved
              </span>
            )}
            <button
              onClick={() => handleSave()}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all"
            >
              {loading ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Save Draft
            </button>
            <button
              onClick={() => handleSave('published')}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-cyan-500 hover:opacity-90 rounded-lg transition-all shadow-sm"
            >
              {loading ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {form?.status === 'published' ? 'Update' : 'Publish'}
            </button>
          </div>
        </div>
      </div>

      {/* Main layout */}
      <div className="max-w-7xl mx-auto px-4 pt-32 pb-12 flex gap-6">
        {/* Editor column */}
        <div className="flex-1 min-w-0 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <AlertCircle size={14} />
              {error}
              <button onClick={() => setError(null)} className="ml-auto"><X size={14} /></button>
            </div>
          )}

          {/* Title */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <input
              type="text"
              placeholder="Post title..."
              value={form?.title}
              onChange={handleTitleChange}
              className="w-full text-2xl font-bold text-gray-900 placeholder-gray-300 border-none outline-none resize-none bg-transparent"
              style={{ fontFamily: 'Crimson Text, serif' }}
            />
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-400">Slug:</span>
              <input
                type="text"
                value={form?.slug}
                onChange={e => handleFormChange('slug', e?.target?.value)}
                placeholder="post-url-slug"
                className="flex-1 text-xs text-violet-600 bg-violet-50 px-2 py-1 rounded-lg border border-violet-100 focus:outline-none focus:ring-1 focus:ring-violet-400 font-mono"
              />
            </div>
          </div>

          {/* Cover image */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-gray-700">Cover Image</span>
              <label className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-violet-600 bg-violet-50 hover:bg-violet-100 rounded-lg cursor-pointer transition-all">
                {coverUploading ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
                {coverUploading ? 'Uploading...' : 'Upload Image'}
                <input type="file" accept="image/*" className="hidden" onChange={handleCoverImageUpload} />
              </label>
            </div>
            {form?.cover_image_url ? (
              <div className="relative">
                <img
                  src={form?.cover_image_url}
                  alt={form?.cover_image_alt || 'Cover'}
                  className="w-full h-48 object-cover rounded-xl"
                />
                <button
                  onClick={() => setForm(prev => ({ ...prev, cover_image_url: '' }))}
                  className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-sm hover:bg-red-50 transition-colors"
                >
                  <X size={14} className="text-gray-500" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-violet-300 hover:bg-violet-50/30 transition-all">
                <ImageIcon size={24} className="text-gray-300 mb-2" />
                <span className="text-xs text-gray-400">Click to upload cover image</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleCoverImageUpload} />
              </label>
            )}
            {form?.cover_image_url && (
              <input
                type="text"
                placeholder="Alt text for cover image (for SEO & accessibility)"
                value={form?.cover_image_alt}
                onChange={e => handleFormChange('cover_image_alt', e?.target?.value)}
                className="mt-2 w-full text-xs px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-400"
              />
            )}
          </div>

          {/* Rich Text Editor */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Toolbar */}
            <div className="border-b border-gray-100 p-2 flex items-center gap-1 flex-wrap bg-gray-50">
              {/* Mode toggle */}
              <div className="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden mr-2">
                <button
                  onClick={() => { syncFromEditor(); setMode('visual'); }}
                  className={`px-3 py-1.5 text-xs font-semibold transition-all ${mode === 'visual' ? 'bg-violet-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  Visual
                </button>
                <button
                  onClick={() => { syncFromEditor(); setMode('html'); }}
                  className={`px-3 py-1.5 text-xs font-semibold transition-all ${mode === 'html' ? 'bg-violet-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  HTML
                </button>
              </div>

              {mode === 'visual' && (
                <>
                  <div className="flex items-center gap-0.5 border-r border-gray-200 pr-2 mr-1">
                    <ToolBtn onClick={() => insertHeading(1)} title="Heading 1"><Heading1 size={14} /></ToolBtn>
                    <ToolBtn onClick={() => insertHeading(2)} title="Heading 2"><Heading2 size={14} /></ToolBtn>
                    <ToolBtn onClick={() => insertHeading(3)} title="Heading 3"><Heading3 size={14} /></ToolBtn>
                  </div>
                  <div className="flex items-center gap-0.5 border-r border-gray-200 pr-2 mr-1">
                    <ToolBtn onClick={() => execCmd('bold')} title="Bold"><Bold size={14} /></ToolBtn>
                    <ToolBtn onClick={() => execCmd('italic')} title="Italic"><Italic size={14} /></ToolBtn>
                    <ToolBtn onClick={() => execCmd('underline')} title="Underline"><Underline size={14} /></ToolBtn>
                  </div>
                  <div className="flex items-center gap-0.5 border-r border-gray-200 pr-2 mr-1">
                    <ToolBtn onClick={() => execCmd('insertUnorderedList')} title="Bullet List"><List size={14} /></ToolBtn>
                    <ToolBtn onClick={() => execCmd('insertOrderedList')} title="Numbered List"><ListOrdered size={14} /></ToolBtn>
                    <ToolBtn onClick={insertBlockquote} title="Blockquote"><Quote size={14} /></ToolBtn>
                    <ToolBtn onClick={insertCode} title="Code Block"><Code size={14} /></ToolBtn>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <ToolBtn onClick={insertLink} title="Insert Link"><Link2 size={14} /></ToolBtn>
                    <label title="Insert Image" className="p-1.5 rounded-md text-gray-600 hover:bg-violet-50 hover:text-violet-600 cursor-pointer transition-all">
                      {imageUploading ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleInlineImageUpload} />
                    </label>
                  </div>
                </>
              )}
            </div>

            {/* Editor area */}
            {mode === 'visual' ? (
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={syncFromEditor}
                onBlur={syncFromEditor}
                className="min-h-96 p-6 text-gray-800 text-base leading-relaxed focus:outline-none prose-editor"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  lineHeight: '1.75',
                }}
                data-placeholder="Start writing your post..."
              />
            ) : (
              <textarea
                value={htmlContent}
                onChange={e => setHtmlContent(e?.target?.value)}
                className="w-full min-h-96 p-6 text-sm font-mono text-gray-700 bg-gray-950 text-green-400 focus:outline-none resize-none"
                placeholder="<p>Write your HTML here...</p>"
                spellCheck={false}
              />
            )}
          </div>

          {/* Excerpt */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Excerpt</label>
            <textarea
              value={form?.excerpt}
              onChange={e => handleFormChange('excerpt', e?.target?.value)}
              placeholder="A short summary shown on the blog listing page..."
              rows={3}
              className="w-full text-sm text-gray-700 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 resize-none transition-all"
            />
            <p className="text-xs text-gray-400 mt-1">{form?.excerpt?.length || 0} / 200 characters recommended</p>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-72 flex-shrink-0 space-y-4">
          {/* Publish settings */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FileText size={14} className="text-violet-500" /> Post Settings
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Status</label>
                <select
                  value={form?.status}
                  onChange={e => handleFormChange('status', e?.target?.value)}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Category</label>
                <select
                  value={form?.category}
                  onChange={e => handleFormChange('category', e?.target?.value)}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                >
                  {CATEGORIES?.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Tag size={14} className="text-violet-500" /> Tags
            </h3>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {form?.tags?.map(tag => (
                <span key={tag} className="flex items-center gap-1 text-xs bg-violet-50 text-violet-700 px-2 py-1 rounded-full border border-violet-100">
                  #{tag}
                  <button onClick={() => removeTag(tag)} className="hover:text-red-500 transition-colors">
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-1.5">
              <input
                type="text"
                value={tagInput}
                onChange={e => setTagInput(e?.target?.value)}
                onKeyDown={e => { if (e?.key === 'Enter' || e?.key === ',') { e?.preventDefault(); addTag(tagInput); } }}
                placeholder="Add tag..."
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400"
              />
              <button
                onClick={() => addTag(tagInput)}
                className="p-1.5 bg-violet-50 text-violet-600 rounded-lg hover:bg-violet-100 transition-all"
              >
                <Plus size={13} />
              </button>
            </div>
            <div className="mt-3">
              <p className="text-xs text-gray-400 mb-1.5">Suggested:</p>
              <div className="flex flex-wrap gap-1">
                {SUGGESTED_TAGS?.filter(t => !form?.tags?.includes(t))?.slice(0, 8)?.map(tag => (
                  <button
                    key={tag}
                    onClick={() => addTag(tag)}
                    className="text-xs text-gray-500 bg-gray-100 hover:bg-violet-50 hover:text-violet-600 px-2 py-0.5 rounded-full transition-all"
                  >
                    +{tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* SEO Tools */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <button
              onClick={() => setSeoOpen(!seoOpen)}
              className="w-full flex items-center justify-between p-5 text-sm font-bold text-gray-800 hover:bg-gray-50 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Globe size={14} className="text-violet-500" /> SEO Tools
              </span>
              {seoOpen ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
            </button>

            {seoOpen && (
              <div className="px-5 pb-5 space-y-3 border-t border-gray-100">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    SEO Title
                    <span className={`ml-1 font-normal ${seoTitleLength > 60 ? 'text-red-500' : 'text-gray-400'}`}>
                      ({seoTitleLength}/60)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={form?.seo_title}
                    onChange={e => handleFormChange('seo_title', e?.target?.value)}
                    placeholder={form?.title || 'SEO page title...'}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-violet-400"
                  />
                  <div className={`h-1 mt-1 rounded-full ${seoTitleLength === 0 ? 'bg-gray-100' : seoTitleLength <= 60 ? 'bg-emerald-400' : 'bg-red-400'}`}
                    style={{ width: `${Math.min(100, (seoTitleLength / 60) * 100)}%` }} />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Meta Description
                    <span className={`ml-1 font-normal ${seoDescLength > 160 ? 'text-red-500' : 'text-gray-400'}`}>
                      ({seoDescLength}/160)
                    </span>
                  </label>
                  <textarea
                    value={form?.seo_description}
                    onChange={e => handleFormChange('seo_description', e?.target?.value)}
                    placeholder={form?.excerpt || 'Meta description for search engines...'}
                    rows={3}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-violet-400 resize-none"
                  />
                  <div className={`h-1 mt-1 rounded-full ${seoDescLength === 0 ? 'bg-gray-100' : seoDescLength <= 160 ? 'bg-emerald-400' : 'bg-red-400'}`}
                    style={{ width: `${Math.min(100, (seoDescLength / 160) * 100)}%` }} />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Keywords</label>
                  <input
                    type="text"
                    value={form?.seo_keywords}
                    onChange={e => handleFormChange('seo_keywords', e?.target?.value)}
                    placeholder="keyword1, keyword2, keyword3"
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-violet-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">OG Image URL</label>
                  <input
                    type="text"
                    value={form?.og_image_url}
                    onChange={e => handleFormChange('og_image_url', e?.target?.value)}
                    placeholder={form?.cover_image_url || 'https://...'}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-violet-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Canonical URL</label>
                  <input
                    type="text"
                    value={form?.canonical_url}
                    onChange={e => handleFormChange('canonical_url', e?.target?.value)}
                    placeholder="https://angle.dating/blog/..."
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-violet-400"
                  />
                </div>

                {/* SERP Preview */}
                <div className="mt-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                  <p className="text-xs font-semibold text-gray-500 mb-2">Search Preview</p>
                  <p className="text-sm text-blue-700 font-medium truncate">
                    {form?.seo_title || form?.title || 'Post Title'}
                  </p>
                  <p className="text-xs text-green-700 truncate">
                    angle.dating/blog/{form?.slug || 'post-slug'}
                  </p>
                  <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">
                    {form?.seo_description || form?.excerpt || 'Meta description will appear here...'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .prose-editor:empty:before {
          content: attr(data-placeholder);
          color: #d1d5db;
          pointer-events: none;
        }
        .prose-editor h1 { font-size: 1.75rem; font-weight: 700; margin: 1rem 0 0.5rem; font-family: 'Crimson Text', serif; }
        .prose-editor h2 { font-size: 1.4rem; font-weight: 700; margin: 1rem 0 0.5rem; font-family: 'Crimson Text', serif; }
        .prose-editor h3 { font-size: 1.15rem; font-weight: 600; margin: 0.75rem 0 0.4rem; }
        .prose-editor p { margin-bottom: 0.75rem; }
        .prose-editor ul { list-style: disc; padding-left: 1.5rem; margin-bottom: 0.75rem; }
        .prose-editor ol { list-style: decimal; padding-left: 1.5rem; margin-bottom: 0.75rem; }
        .prose-editor blockquote { border-left: 4px solid #7c3aed; padding-left: 1rem; color: #6b7280; font-style: italic; margin: 1rem 0; }
        .prose-editor pre { background: #1f2937; color: #d1fae5; padding: 1rem; border-radius: 0.5rem; font-family: monospace; font-size: 0.85rem; overflow-x: auto; margin: 0.75rem 0; }
        .prose-editor a { color: #7c3aed; text-decoration: underline; }
        .prose-editor img { max-width: 100%; border-radius: 0.5rem; margin: 0.5rem 0; }
      `}</style>
    </div>
  );
};

const ToolBtn = ({ onClick, title, children }) => (
  <button
    onMouseDown={e => { e?.preventDefault(); onClick(); }}
    title={title}
    className="p-1.5 rounded-md text-gray-600 hover:bg-violet-50 hover:text-violet-600 transition-all"
  >
    {children}
  </button>
);

export default BlogEditorPage;
